import { Card, Select, Text, TextField } from '@nike/eds';
import { useI18nContext } from '../../../../context/language/use-i18n-context';
import { Field, FormikProvider, useFormik } from 'formik';
import { FC, useEffect, useState } from 'react';
import { ts } from '../i18n';
import { useUserContext } from '../../../../context/user';
import {
  dateFormat,
  getMinDate,
  getSetDates,
  getUserShortDate,
} from '../../../../utils/date-helper';
import { PassPolicy } from '../../../../models/IPassPolicy';
import { StoreOption, VendorPass } from '../type';
import * as Yup from 'yup';
import { AppConfig } from '../../../../utils/constants';
import translations from '../../../shared/i18n';
import styles from '../vendor-pass.module.css';
import { GetPassLocations } from 'services/pass-location.service';
import { sortPassLocations } from 'utils/helper';

type Props = {
  vendorFormValues: VendorPass;
  passPolicy: PassPolicy;
  handleFormChanges: (vendorPass: VendorPass) => void;
};
type TsKeys = keyof typeof translations;
export const VendorForm: FC<Props> = ({ vendorFormValues, passPolicy, handleFormChanges }) => {
  const { i18nString } = useI18nContext();
  const userInfo = useUserContext();
  const [dateRange, setDateRangeValue] = useState<string>('');
  const [passLocations, setPassLocations] = useState<StoreOption[]>([]);
  const isSingleDay =
    dateFormat(passPolicy.shoppingStartDate) === dateFormat(passPolicy.shoppingEndDate);

  const validationSchema = Yup.object().shape({
    companyName: Yup.string()
      .required()
      .matches(
        RegExp(AppConfig.regexPatterns.regexPersonNames),
        AppConfig.validationMessages.namesErrorMessage,
      ),
    passLocationId: Yup.number().required(),
    shoppingDateStart: isSingleDay ? Yup.string().notRequired() : Yup.string().required(),
  });
  const formik = useFormik({
    validateOnMount: true,
    initialValues: vendorFormValues,
    enableReinitialize: true,
    onSubmit: () => {
      formik.resetForm();
    },
    validationSchema: validationSchema,
  });
  useEffect(() => {
    if (passPolicy) {
      GetPassLocations(passPolicy?.passLocationGroupId).then(response => {
        // const selectStore: StoreOption= { value: 0, label: i18nString(ts.DDL_STORE_SELECT),shoppingDays: 0 };
        const storeOptions = sortPassLocations(response.data);
        setPassLocations(storeOptions);
        if (storeOptions.length === 1) {
          formik.setFieldValue('passLocationId', storeOptions[0].value);
        }
        
        //set default date alert based on shopping days
        if(dateRange=='')setDefaultDateAlert();
        validateDate();
      });
    }
  }, [passPolicy]);

  const setDefaultDateAlert = () => {
    if (passPolicy?.shoppingDays == 1) {
      setDateAlert('PASS_MODAL_DATE_RANGE_SINGLE', passPolicy.shoppingDays);
    } else {
      setDateAlert('PASS_MODAL_DATE_RANGE_MULTIPLE', passPolicy.shoppingDays);
    }
  };

  const handleDateChange = (event: any) => {
    formik.setFieldValue('shoppingDateEnd', event.target.value);
    formik.handleChange(event);
    onDateChange(event.target.value);
  };

  const onDateChange = (startDate: string) => {
    const shoppingDays =
      passLocations.find(x => x.value === formik.values.passLocationId)?.shoppingDays || 0;
    const dateObj = getSetDates(
      startDate,
      formik.values.shoppingDateEnd,
      passPolicy,
      shoppingDays,
      userInfo?.passplayUser?.userCountry || '',
    );
    if (dateObj.isValid) {
      formik.setFieldValue('shoppingDateEnd', dateObj.endDate);
    } else {
      setDate();
    }
    if (dateObj.dateAlertText !== '') {
      setDateAlert(dateObj.dateAlertText, dateObj.shoppingDays);
    } else setDateRangeValue('');
  };

  const validateDate = () => {
    if (!!vendorFormValues.shoppingDateStart && vendorFormValues.shoppingDateStart !== '') {
      const shoppingDays =
        passLocations.find(x => x.value === formik.values.passLocationId)?.shoppingDays || 0;
      const dateObj = getSetDates(
        vendorFormValues.shoppingDateStart,
        vendorFormValues.shoppingDateEnd,
        passPolicy,
        shoppingDays,
        userInfo?.passplayUser?.userCountry || '',
      );
      if (!dateObj.isValid) {
        setDate();
      }
      if (dateObj.dateAlertText !== '') setDateAlert(dateObj.dateAlertText, dateObj.shoppingDays);
      else setDateRangeValue('');
    }
  };
  const setDate = () => {
    formik.setFieldValue('shoppingDateStart', '', true);
    formik.setFieldValue('shoppingDateEnd', '');
  };
  const setDateAlert = (dateAlertText: string, shoppingDays: number) => {
    const alertText = i18nString(translations[dateAlertText as TsKeys]);
    setDateRangeValue(alertText.replace('{{number}}', shoppingDays.toString()));
  };
  useEffect(() => {
    handleFormChanges(formik.values);
  }, [formik.values]);
  return (
    <section>
      <FormikProvider value={formik}>
        <Card className="section-border form_wrapper">
          <Text className="h4_font eds-spacing--mt-12 eds-spacing--mt-4" as={'h4'}>
            {i18nString(ts.PASS_MODAL_VENDOR_HEADING)}
          </Text>
          <TextField
            id="companyName"
            label={i18nString(ts.PLACEHOLDER_VENDOR_CODE_COMPANY_NAME)}
            aria-label={i18nString(ts.PLACEHOLDER_VENDOR_CODE_COMPANY_NAME)}
            hideLabel={true}
            autoFocus={true}
            placeholder={i18nString(ts.PLACEHOLDER_VENDOR_CODE_COMPANY_NAME)}
            type="text"
            name="companyName"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="eds-spacing--mt-12"
            value={formik.values.companyName}
            data-testid="company_name"
            hasErrors={!!formik.errors.companyName && !!formik.touched.companyName}
            errorMessage=' '
          />
          <div className="eds-flex eds-flex--direction-row eds-flex--wrap eds-flex--align-content-flex-start eds-flex--justify-content-space-between">
            <div className={`${styles.column}`}>
              <Text className="h4_font eds-spacing--mt-12 eds-spacing--mx-4" as={'h4'}>
                {i18nString(ts.PASS_MODAL_DATE_RANGE_HEADING)}
              </Text>
              <div className="eds-flex eds-flex--justify-content-space-between eds-flex--direction-row eds-flex--noWrap eds-spacing--mt-16 row">
                <div className={`input__container ${styles.input_width}`}>
                  <TextField
                    label="shoppingDateStart"
                    hideLabel={true}
                    aria-label="shoppingDateStart"
                    disabled={isSingleDay}
                    type="date"
                    data-testid="shoppingDateStart"
                    value={formik.values.shoppingDateStart}
                    onChange={e => {
                      handleDateChange(e);
                    }}
                    onBlur={formik.handleBlur}
                    min={getMinDate()}
                    id="shoppingDateStart"
                    placeholder={getUserShortDate(userInfo?.passplayUser?.userCountry || '')}
                    errorMessage={formik.values.shoppingDateStart}
                  />
                </div>
                <div className={`input__container ${styles.input_width}`}>
                  <TextField
                    label="shoppingDateEnd"
                    hideLabel={true}
                    disabled={true}
                    aria-label="shoppingDateEnd"
                    name="shoppingDateEnd"
                    type="date"
                    placeholder={getUserShortDate(userInfo?.passplayUser?.userCountry || '')}
                    value={formik.values.shoppingDateEnd}
                    id="shoppingDateEnd"
                    errorMessage={formik.values.shoppingDateEnd}
                  />
                </div>
              </div>
              {passPolicy.shoppingStartDate && (
                <p id="dateRange" className="text--help-text">
                  {dateRange}
                </p>
              )}
            </div>
            <div className={`${styles.column}`}>
              <Text className="h4_font eds-spacing--mt-12 eds-spacing--mx-4" as={'h4'}>
                {i18nString(ts.PASS_MODAL_STORE_HEADING)}
              </Text>
              <Field name="passLocationId">
                {({ field }: { field: any }) => (
                  <Select
                    {...field}
                    id="passLocationId"
                    data-testid="passLocationId"
                    options={passLocations}
                    label="store"
                    aria-label="store"
                    name="passLocationId"
                    className="guest__form-card eds-spacing--mt-16"
                    value={
                      passLocations.find(option => option.value === formik.values.passLocationId) ||
                      ''
                    }
                    onChange={(option: any) => {
                      formik.setFieldValue('passLocationId', option.value);
                    }}
                    hideLabel={true}
                    onBlur={formik.handleBlur}
                    isSearchable={false}
                    hasErrors={formik.touched.passLocationId && !!formik.errors.passLocationId}
                    errorMessage={
                      typeof formik.errors.passLocationId === 'string'
                        ? formik.errors.passLocationId
                        : undefined
                    }
                  />
                )}
              </Field>
            </div>
          </div>
        </Card>
      </FormikProvider>
    </section>
  );
};
